"use client";
import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Text } from "@/components/ui/typography";
import { Sparkles, Loader2, Lightbulb, RefreshCw, Smartphone, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Pre-recording guidance + AI teleprompter.
 *
 * The teleprompter script is generated server-side from the candidate's OWN
 * resume (POST /api/video-resume/script) so they never freeze wondering what
 * to say — they read a personalized 60-90s script.
 */
const RECORDING_TIPS = [
  "Find a quiet spot with light on your face (face a window)",
  "Put the camera at eye level and look into the lens",
  "Speak a little slower than feels natural",
  "Aim for 60–90 seconds — short and confident beats long",
  "Smile when you introduce yourself",
];

function VideoGuide({ resumeData, onVideoArrived }) {
  const [script, setScript] = useState(null); // {sections:[{title,text}], estimated_seconds}
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ── Phone QR handoff state ────────────────────────────────────────────
  const [qrToken, setQrToken] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [phoneUploaded, setPhoneUploaded] = useState(false);
  const pollRef = useRef(null);
  const baselineFilenameRef = useRef(null);

  const startPhoneHandoff = async () => {
    setQrLoading(true);
    try {
      // Snapshot the current video (if any) so polling can detect a NEW one.
      try {
        const cur = await fetch("/api/check-video", { credentials: "include" });
        const curJson = await cur.json();
        baselineFilenameRef.current = curJson?.filename || null;
      } catch { baselineFilenameRef.current = null; }

      const resp = await fetch("/api/video-resume/mobile-token", {
        method: "POST",
        credentials: "include",
      });
      const json = await resp.json();
      if (resp.ok && json.ok) {
        setQrToken(json.token);
        setPhoneUploaded(false);
      }
    } finally {
      setQrLoading(false);
    }
  };

  // Poll for the phone's upload while the QR is showing.
  useEffect(() => {
    if (!qrToken || phoneUploaded) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const resp = await fetch("/api/check-video", { credentials: "include" });
        const json = await resp.json();
        if (json?.hasVideo && json.filename !== baselineFilenameRef.current) {
          setPhoneUploaded(true);
          clearInterval(pollRef.current);
          pollRef.current = null;
          onVideoArrived?.(json);
        }
      } catch { /* transient — keep polling */ }
    }, 4000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [qrToken, phoneUploaded, onVideoArrived]);

  const generateScript = async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await fetch("/api/video-resume/script", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ resume_data: resumeData || {} }),
      });
      const json = await resp.json();
      if (!resp.ok || !json.ok) {
        setError(json.error || "Could not generate a script right now.");
        return;
      }
      setScript(json);
    } catch (e) {
      setError("Could not generate a script right now.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cn("w-full space-y-4")}>
      {/* Recording tips */}
      <Card className={cn("border-border")}>
        <CardContent className={cn("p-4")}>
          <div className={cn("flex items-center gap-2 mb-3")}>
            <Lightbulb className="w-4 h-4 text-warning" />
            <Text variant="small" className={cn("font-semibold text-foreground")}>
              Before you record
            </Text>
          </div>
          <ul className={cn("space-y-1.5")}>
            {RECORDING_TIPS.map((tip, i) => (
              <li key={i} className={cn("flex items-start gap-2 text-sm text-muted-foreground")}>
                <span className={cn("text-success mt-0.5")}>✓</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* AI teleprompter */}
      <Card className={cn("border-primary/30 bg-primary/5")}>
        <CardContent className={cn("p-4")}>
          <div className={cn("flex items-center justify-between mb-3")}>
            <div className={cn("flex items-center gap-2")}>
              <Sparkles className="w-4 h-4 text-primary" />
              <Text variant="small" className={cn("font-semibold text-foreground")}>
                Your personal script
              </Text>
            </div>
            {script && (
              <Badge variant="secondary" className={cn("text-xs")}>
                ~{script.estimated_seconds}s
              </Badge>
            )}
          </div>

          {!script && (
            <div className={cn("space-y-3")}>
              <Text variant="muted" className={cn("text-sm")}>
                Not sure what to say? We&apos;ll write a short script from your
                resume — just read it naturally while recording.
              </Text>
              <Button
                onClick={generateScript}
                disabled={loading}
                size="sm"
                className={cn("gap-2 w-full")}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Writing your script…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generate my script with AI
                  </>
                )}
              </Button>
              {error && (
                <Text className={cn("text-sm text-destructive")}>{error}</Text>
              )}
            </div>
          )}

          {script && (
            <div className={cn("space-y-4")}>
              <div className={cn(
                "max-h-[320px] overflow-y-auto rounded-lg bg-background/80 p-4 space-y-4",
                "border border-border"
              )}>
                {(script.sections || []).map((s, i) => (
                  <div key={i}>
                    {s.title && (
                      <Text variant="small" className={cn("font-semibold text-primary mb-1 uppercase tracking-wide text-xs")}>
                        {s.title}
                      </Text>
                    )}
                    <Text className={cn("text-base leading-relaxed text-foreground")}>
                      {s.text}
                    </Text>
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={generateScript}
                disabled={loading}
                className={cn("gap-2")}
              >
                {loading
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <RefreshCw className="w-4 h-4" />}
                Rewrite script
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Record-on-phone QR handoff */}
      <Card className={cn("border-border")}>
        <CardContent className={cn("p-4")}>
          <div className={cn("flex items-center gap-2 mb-2")}>
            <Smartphone className="w-4 h-4 text-primary" />
            <Text variant="small" className={cn("font-semibold text-foreground")}>
              Record on your phone instead
            </Text>
          </div>

          {phoneUploaded ? (
            <div className={cn("flex items-center gap-2 text-success")}>
              <CheckCircle2 className="w-5 h-5" />
              <Text className={cn("text-sm font-medium")}>
                Got it! Your phone video is saved.
              </Text>
            </div>
          ) : !qrToken ? (
            <div className={cn("space-y-3")}>
              <Text variant="muted" className={cn("text-sm")}>
                Phone cameras look better — scan a QR, record with your
                phone&apos;s camera, and it lands here automatically.
              </Text>
              <Button
                variant="outline"
                size="sm"
                onClick={startPhoneHandoff}
                disabled={qrLoading}
                className={cn("gap-2 w-full")}
              >
                {qrLoading
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Smartphone className="w-4 h-4" />}
                Show QR code
              </Button>
            </div>
          ) : (
            <div className={cn("space-y-3 text-center")}>
              <div className={cn("bg-white rounded-lg p-3 inline-block mx-auto")}>
                <img
                  src={`/api/video-resume/mobile-token/${qrToken}/qr.svg`}
                  alt="Scan with your phone to record"
                  className={cn("w-44 h-44")}
                />
              </div>
              <Text variant="muted" className={cn("text-xs")}>
                Scan with your phone camera · link valid for 15 minutes ·
                waiting for your upload…
              </Text>
              <div className={cn("flex justify-center")}>
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default VideoGuide;
